import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

const baseQuery = fetchBaseQuery({
  // The default query prefix for the API is `/api`
  baseUrl: "/api",
  prepareHeaders: (headers, { getState }) => {
    // Add the access token to API requests when available
    const accessToken = getState().auth.accessToken;
    if (accessToken) {
      headers.set("authorization", `Bearer ${accessToken}`);
    }

    return headers;
  },
});

// Define the single API slice object for the `api` service
export const api = createApi({
  // The cache reducer expects to be added at `state.api` (the default)
  reducerPath: "api",

  baseQuery,

  // The `endpoints` represent operations and requests for this service.
  // The baseline endpoints are implemented here that are common to all
  // features. The app features extend this service with injectEndpoints().
  endpoints: (builder) => ({
    // Connectivity health check with api service
    getHealth: builder.query({
      query: () => "/health",
    }),

    // Create User from UI
    createUser: builder.mutation({
      query: (p) => ({
        url: "/create_web_user",
        method: "POST",
        params: {'username': p.username, 'password': p.password, 'age': p.age}
      })
    }),

    // Login request
    login: builder.mutation({
      query: (credentials) => ({
        url: "/login",
        method: "POST",
        headers: {
          "content-type": "application/x-www-form-urlencoded",
        },
        body: new URLSearchParams(credentials).toString(),
      }),
    }),

    loginCodeValid: builder.mutation({
      query: (credentials) => ({
        url: "/login_code_valid",
        method: "POST",
        headers: {
          "content-type": "application/x-www-form-urlencoded",
        },
        body: new URLSearchParams(credentials).toString(),
      }),
    }),

    adminRole: builder.mutation({
      query: (p) => ({
        url: "/admin_role",
        method: "POST",
        params:{'uuid': p.uuid}
      }),
    }),

    adminLogin: builder.mutation({
      query: (credentials) => ({
        url: "/login_admin",
        method: "POST",
        headers: {
          "content-type": "application/x-www-form-urlencoded",
        },
        body: new URLSearchParams(credentials).toString(),
      }),
    }),

    enable2fa: builder.mutation({
      query: (p) => ({
        url: "/enable_2fa",
        method: "POST",
        params: p
      }),
    }),

    check2faCode: builder.mutation({
      query: (p) => ({
        url: "/check_2fa_code",
        method: "POST",
        params: p
      }),
    }),

    contestImages: builder.query({
      query: (p) => ({
        url: "/get_contest_images",
        params:{'contest': p.contest}
      })
    }),

    liveStreamModelCounts: builder.query({
      query: () => "/live_cams_model_streams_for_ui",
    }),

    liveStreams: builder.query({
      query: () => "/live_stream_history",
    }),

    runSingleAml: builder.mutation({
      query: (p) => ({
        url: "/run_single_aml",
        method: "POST",
        params: {
          'run_type': p.run_type ,
          'account_type': p.account_type ,
          'account_id': p.account_id , 
          'first_name': p.first_name ,
          'last_name': p.last_name ,
          'business_name': p.business_name ,
          'country': p.country ,
          'admin_uuid': p.admin_uuid ,
          'admin_note': p.admin_note  ,
          'client_id': p.client_id  
        }
      }),
    }),

    postAutoAML: builder.mutation({
      query: (p) => ({
        url: "/post_auto_aml",
        method: "POST",
        body: p
      }),
    }),

    getPastAMLRuns: builder.query({
      query: () => "/past_aml_runs",
    }),

    startAccountUpdate: builder.mutation({
      query: (p) => ({
        url: "/run_account_updates",
        method: "POST",
        params: {
          'account_type': p.account_type,
          'status_flag': p.status_flag ,
          'admin_uuid': p.admin_uuid ,
          'admin_note': p.admin_note ,
        }
      }),
    }),

    sendEmail: builder.mutation({
      query: (p) => ({
        url: "/send_email",
        method: "POST",
        params: {'recipient':p.recipient},
      })
    }),

    captureContract: builder.mutation({
      query: (p) => ({
        url: "/capture-contract",
        method: "POST",
        body: p,
      })
    }),

    captureContractFromHtml: builder.mutation({
      query: (p) => ({
        url: "/capture-contract-from-html",
        method: "POST",
        body: p,
      })
    }),

    uploadS3: builder.mutation({
      query: (p) => ({
        url: "/s3-upload-internal-image",
        method: "POST",
        params: {'img_type':p.img_type, 
          'description': p.description, 
          'category': p.category, 
          'account_id':p.account_id, 
          'account_type':p.account_type, 
          'original_filename':p.original_filename},
        body: p.body,
        formData: true,
      })
    }),

    uploadKYBS3: builder.mutation({
      query: (p) => ({
        url: "/s3-upload-url-kyb",
        method: "POST",
        params: {'img_type':p.img_type, 
          'description': p.description, 
          'category': p.category,
          'full_path': p.full_path, 
          'filename':p.filename},
        body: p.body,
        formData: true,
      })
    }),

    kybFormSubmission: builder.mutation({
      query: (p) => ({
        url: "/kyb_form_submission",
        method: "POST",
        body: p 
      })
    }),

    deleteImage: builder.mutation({
      query: (p) => ({
        url: "/delete_image",
        method: "POST",
        params: {'image_id': p.image_id} 
      })
    }),

    editImage: builder.mutation({
      query: (p) => ({
        url: "/edit_image",
        method: "POST",
        params: {'image_id': p.image_id, "title": p.title, "description": p.description} 
      })
    }),
    
    // voting
    castVote: builder.mutation({
      query: (p) => ({
        url: "/vote",
        method: "POST",
        params: {'image_id': p.image_id, 'uuid': p.uuid}
      })
    }),

    removeVote: builder.mutation({
      query: (p) => ({
        url: "/remove_vote",
        method: "POST",
        params: {'image_id': p.image_id, 'uuid': p.uuid}
      })
    }),

    countUserVotes: builder.query({
      query: (p) => ({
        url: "/get_user_votes",
        params: {'uuid': p.uuid, 'contest': p.contest}
      })
    }),

    checkCode: builder.mutation({
      query: (p) => ({
        url: "/check_code",
        method: "POST",
        params: {'recipient': p.recipient, 'code': p.code}
      })
    }),

    getConfig: builder.query({
      query: (p) => ({
        url: "/get_config",
        params: {'name': p.name}
      })
    }),

    getKYB: builder.query({
      query: () => ({
        url: "/get_kyb",
      })
    }),

    getKYBImages: builder.mutation({
      query: (p) => ({
        url: "/get_kyb_images",
        method: "POST",
        params: {'kyb_display': p.kyb_display}
      })
    }),

    getKYBResultByAccountAndType: builder.mutation({
      query: (p) => ({
        url: "/get_kyb_result_by_account_and_type",
        method: "POST",
        params: {'account_id': p.account_id, 'account_type': p.account_type}
      })
    }),

    

    getImages: builder.mutation({
      query: (p) => ({
        url: "/get_images",
        method: "POST",
        params: {'account_id': p.account_id, 'account_type': p.account_type}
      })
    }),

    getAllPartners: builder.query({
      query: () => ({
        url: "/get_all_partners",
      })
    }),

    getVerifyAccountsByType: builder.query({
      query: (p) => ({
        url: "/type_verify_accounts",
        params: {'account_type': p.account_type}
      })
    }),

    updatePartner: builder.mutation({
      query: (p) => ({
        url: "/update_partner_approval",
        method: "POST",
        params: {'kyb': p.kyb, 'approved': p.approved, 'note': p.note, 'deny': p.deny}
      })
    }),

    getAllMemberStats: builder.query({
      query: () => ({
        url: "/get_all_member_stats",
      })
    }),

    getAllMembersFor: builder.mutation({
      query: (p) => ({
        url: "/get_all_members_for",
        method: "POST",
        params: {'run_type': p.run_type}
      })
    }),

    getVerifyAccountCounts: builder.query({
      query: () => ({
        url: "/account_counts",
      })
    }),

    //** DEPRECATED! - use editVerify **//
    updateVerifyAccountStatus: builder.mutation({
      query: (p) => ({
        url: "/update_verify_account_status",
        method: "POST",
        params: {'account_type': p.account_type, 'account_id':p.account_id, 
          'overall_status': p.overall_status, 'overall_notes': p.overall_notes,
          'overall_status_by': p.overall_status_by}
      })
    }),

    editVerifyStatus: builder.mutation({
      query: (p) => ({
        url: "/edit_verify_status",
        method: "POST",
        params: {'client_id': p.client_id, 'scan_ref': p.scan_ref,
          'override_status': p.override_status, 'type': p.type, 'idenfy_id': p.idenfy_id,
          'admin_user': p.admin_user, 'admin_comment': p.admin_comment
        }
      })
    }),

    search: builder.mutation({
      query: (p) => ({
        url: "/search",
        method: "POST",
        params: {'search_string': p.search_string, 
          'search_type': p.search_type,
           'is_single_account': (p.is_single_account || 0)
          }
      })
    }),

    getStudioCounts: builder.query({
      query: () => ({
        url: "/studio_counts",
      })
    }),


    getStatusHistory: builder.mutation({
      query: (p) => ({
        url: "/get_all_kyc_history",
        method: "POST",
        params: {'account': p.account, 'company_id': p.company_id}
      })
    }),

    getIdenfyResponse: builder.query({
      query: (p) => ({
        url: "/get_idenfy_response",
        params: {'table': p.table, 'id': p.id}
      })
    }),

    getKYCSents: builder.mutation({
      query: (p) => ({
        url: "/get_kyc_sents",
        method: "POST",
        params: p
      })
    }),

    postKYCSent: builder.mutation({
      query: (p) => ({
        url: "/post_kyc_sent",
        method: "POST",
        params: p
      })
    }),

    uploadExcel: builder.mutation({
      query: ({file, accountType}) => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('account_type', accountType);
        return {
          url: '/upload_bulk_aml/',
          method: 'POST',
          body: formData,
        };
      },
    }),

    uploadDacExcel: builder.mutation({
      query: (formData) => ({
        url: '/upload_dac/',
        method: 'POST',
        body: formData,
        responseHandler: async (response) => {
          // Check if it's an Excel file
          const contentType = response.headers.get('content-type');
          if (contentType && contentType.includes('spreadsheet')) {
            return response.blob();
          }
          // Otherwise it's probably an error (JSON)
          return response.json();
        },
      }),
    }),

    getAMLIndividualHistory: builder.query({
      query: (p) => ({
        url: "/aml_individual_history",
        params: {'account_type': p.account_type, 
          'account_id': p.account_id
        }
      })
    }),

    getAMLBusinessHistory: builder.query({
      query: (p) => ({
        url: "/aml_business_history",
        params: {'account_type': p.account_type, 'account_id': p.account_id}
      })
    }),

    getFailedAccounts: builder.mutation({
      query: (p) => ({
        url: "/failed_accounts",
        method: "POST",
        params: {'account_type': p.account_type, 'status_flag': p.status_flag}
      })
    }),


    launchKYStart: builder.mutation({
      query: (p) => ({
        url: "/launch_ky",
        method: "POST",
        params: p
      })
    }),

    launchKYFromInside: builder.mutation({
      query: (p) => ({
        url: "/launch_ky_from_inside",
        method: "POST",
        params: {'client_id': p.client_id, 'override_verify': p.override_verify}
      })
    }),

    getPresignedURL: builder.mutation({
      query: (p) => ({
        url: "/get_kyb_presigned",
        method: "POST",
        params: {'bucket_name': p.bucket_name, 'object_name': p.object_name}
      })
    }),


    getAllNotes: builder.query({
      query: (p) => ({
        url: "/get_all_notes",
        params: {'account_type': p.account_type, 'account_id': p.account_id}
      })
    }),

    getMentionableUsers: builder.query({
      query: (search = '') => ({
        url: `/mentionable${search ? `?search=${encodeURIComponent(search)}` : ''}`,
        method: 'GET',
      }),
      providesTags: ['MentionableUsers'],
    }),

    addNote: builder.mutation({
      query: (p) => ({
        url: "/add_note",
        method: "POST",
        params: {
          'client_id': p.client_id,
          'account_type': p.account_type,
          'account_id': p.account_id,
          'note': p.note,
          'admin_uuid': p.admin_uuid,
          'image_id': p.image_id,
          'gpid': p.gpid,
          'account_name': p.account_name
        }
      })
    }),

    editNote: builder.mutation({
      query: (p) => ({
        url: "/edit_note",
        method: "POST",
        params: {
          'note_id': p.note_id,
          'account_type': p.account_type,
          'account_id': p.account_id,
          'note': p.note,
          'admin_uuid': p.admin_uuid
        }
      })
    }),

    runPost: builder.mutation({
      query: (p) => ({
        url: "/" + p.url,
        method: "POST",
        params: p
      }) 
    }), 

    runPostWithBody: builder.mutation({
      query: (p) => ({
        url: "/" + p.url,
        method: "POST",
        params: p.params,
        body: p.body,
        formData: true,
      }) 
    }), 


    runGet: builder.query({
      query: (p) => ({
        url: "/" + p.url,
        params: p
      })
    }), 

    getKYBResultsByPaymentStatus: builder.query({
      query: (p) => ({
        url: "/get_kyb_results_by_payment_status",
        params: {
          ...(p?.pay_status && { pay_status: p.pay_status }),
          ...(p?.assigned_to && { assigned_to: p.assigned_to }),
          ...(p?.page && { page: p.page }),
          ...(p?.page_size && { page_size: p.page_size }),
          ...(p?.kyb_status && { kyb_status: p.kyb_status }),
          ...(p?.kyc_status && { kyc_status: p.kyc_status }),
          ...(p?.due_diligence_status && { due_diligence_status: p.due_diligence_status }),
          ...(p?.account_type && { account_type: p.account_type }),
          ...(p?.submission_source && { submission_source: p.submission_source })
        }
      })
    }),

    getIDVResultsWithSummary: builder.query({
      query: (p) => ({
        url: "/get_idv_results_with_summary",
        params: {
          ...(p?.account_id && { account_id: p.account_id }),
          ...(p?.account_type && { account_type: p.account_type }),
          ...(p?.status && { status: p.status }),
          ...(p?.page && { page: p.page }),
          ...(p?.page_size && { page_size: p.page_size })
        }
      })
    }),

    getConfirmIDSummary: builder.query({
      query: () => ({
        url: "/get_confirmid_summary"
      })
    }),

    getConfirmIDDailyCounts: builder.query({
      query: (p) => ({
        url: "/get_confirmid_daily_counts",
        params: {
          ...(p?.days && { days: p.days })
        }
      })
    }),

    getConfirmIDImages: builder.query({
      query: (p) => ({
        url: "/get_confirmid_images",
        params: {
          model_id: p.model_id,
          ...(p?.page && { page: p.page }),
          ...(p?.page_size && { page_size: p.page_size })
        }
      })
    }),

    getRekognitionSummary: builder.query({
      query: () => ({
        url: "/get_rekognition_summary"
      })
    }),

    getRekognitionDailyCounts: builder.query({
      query: (p) => ({
        url: "/get_rekognition_daily_counts",
        params: {
          ...(p?.days && { days: p.days })
        }
      })
    }),
    
    updateProfileAssignment: builder.mutation({
      query: (p) => ({
        url: "/update_profile_assignment",
        method: "POST",
        params: {
          'account_type': p.account_type,
          'account_id': p.account_id,
          'assigned_to': p.assigned_to
        }
      })
    }),
    
    updateUserAssignment: builder.mutation({
      query: (p) => ({
        url: "/update_user_assignment",
        method: "POST",
        params: {
          'account_type': p.account_type,
          'account_id': p.account_id,
          'admin_uuid': p.admin_uuid,
          'account_name': p.account_name
        }
      }),
      invalidatesTags: (result, error, arg) => [
        { type: 'Account', id: `${arg.account_type}:${arg.account_id}` }
      ]
    }),

    updateCompanyName: builder.mutation({
      query: (p) => ({
        url: "/update_company_name",
        method: "POST",
        params: {
          'account_id': p.account_id,
          'account_type': p.account_type,
          'company_name': p.company_name
        }
      }),
      invalidatesTags: ['UserDashboard', 'KYBResults']
    }),

    getAdminUsers: builder.query({
      query: () => ({
        url: "/admin_users"
      })
    }),

    updateAdminRole: builder.mutation({
      query: (p) => ({
        url: "/update_admin_role",
        method: "POST",
        params: {
          'uuid': p.uuid,
          'role': p.role
        }
      })
    }),

    sendKYCEmail: builder.mutation({
      query: (p) => ({
        url: "/send_kyc_email",
        method: "POST",
        body: p
      })
    }),

    sendManualKYCEmail: builder.mutation({
      query: (p) => ({
        url: "/send_manual_kyc_email",
        method: "POST",
        body: p
      })
    }),

    sendManualKYBEmail: builder.mutation({
      query: (p) => ({
        url: "/send_manual_kyb_email",
        method: "POST",
        body: p
      })
    }),

    expiredIdenfyKYC: builder.query({
      query: () => ({
        url: "/expired_kyc_idenfy",
        method: "GET"
      })
    }),

    getExpiredKYBDocuments: builder.query({
      query: () => ({
        url: "/get_expired_kyb_documents",
        method: "GET"
      })
    }),

    resetExpiredKYBDocuments: builder.mutation({
      query: (p) => ({
        url: "/reset_expired_kyb_documents",
        method: "POST",
        params: p
      })
    }),

    getUserDashboard: builder.query({
      query: () => ({
        url: "/user_dashboard",
      }),
      providesTags: ['UserDashboard'],
      refetchOnMountOrArgChange: true
    }),

    postPaxumData: builder.mutation({
      query: (p) => ({
        url: "/post_paxum_data",
        method: "POST",
        params: p
      })
    }),

    getCountryPaymentsConfig: builder.query({
      query: (p) => ({
        url: "/select_country_payments_config",
        method: "GET",
        params: p
      })
    }),

    deleteCountryPaymentConfig: builder.mutation({
      query: (p) => ({
        url: "/delete_country_payments_config",
        method: "DELETE",
        params: p
      })
    }),

    postCountryPaymentConfig: builder.mutation({
      query: (p) => ({
        url: "/insert_country_payment_config",
        method: "POST",
        body: p
      })
    }),

    postEditCountryPaymentConfig: builder.mutation({
      query: (p) => ({
        url: "edit_country_payment_config",
        method: "POST",
        body: p
      })
    }),

    getBannedCountryPaymentsConfig: builder.query({
      query: (p) => ({
        url: "/select_banned_country_payments_config",
        method: "GET",
        params: p
      })
    }),

    getCtoPMaps: builder.query({
      query: () => ({
        url: "/get_ctop_maps",
        method: "GET",
      })
    }),

    getCountryPaymentsConfigHistory: builder.query({
      query: (p) => ({
        url: "/get_country_payments_config_history",
        method: "GET",
        params: p
      })
    }),

    getDAC7Token: builder.query({
      query: (p) => ({
        url: "/get_dac7_token",
        method: "GET",
        params: p
      })
    }),

    checkDAC7Token: builder.query({
      query: (p) => ({
        url: "/check_dac7_token",
        method: "GET",
        params: p
      })
    }),

    getDAC7Data: builder.query({
      query: (p) => ({
        url: "/get_dac7_data",
        method: "GET",
        params: p
      })
    }),

    postDAC7Submission: builder.mutation({
      query: (p) => ({
        url: "/insert_dac7_submission",
        method: "POST",
        body: p
      })
    }),

    postAvailablePaymentConfig: builder.mutation({
      query: (p) => ({
        url: "/add_available_payment_config",
        method: "POST",
        body: p
      })
    }),
    
    getAvailablePaymentConfigs: builder.query({
      query: (p) => ({
        url: "/get_available_payment_configs",
        method: "GET",
        params: p
      })
    }),

    sendDAC7Email: builder.mutation({
      query: (p) => ({
        url: "/send_dac7_message",
        method: "POST",
        body: p
      })
    }),

    getDAC7Activity: builder.query({
      query: (p) => ({
        url: "/get_dac7_activity",
        params: {
          ...(p?.page && { page: p.page }),
          ...(p?.page_size && { page_size: p.page_size }),
          ...(p?.status && { status: p.status }),
          ...(p?.year && { year: p.year }),
          ...(p?.studio_id && { studio_id: p.studio_id }),
          ...(p?.export && { export: p.export })
        }
      })
    }),

    getAdBuys: builder.query({
      query: (p) => ({
        url: "/get_adbuys",
        method: "GET",
        params: p
      })
    }),

    syncPaymentMMI: builder.query({
      query: (p) => ({
        url: "/sync_payment_to_mmi",
        method: "GET",
        params: p
      })
    }),

    getKYBSubmissionJSON: builder.query({
      query: (p) => ({
        url: "/getKYB_json",
        method: "GET",
        params: p
      })
    }),

    runTest1: builder.query({
      query: (p) => ({
        url: "/run_test_1",
        method: "GET",
        params: p
      })
    }),

    getReminders: builder.query({
      query: () => ({
        url: "/get_reminders",
        method: "GET"
      })
    }),

    insertReminder: builder.mutation({
      query: (p) => ({
        url: "/insert_reminder",
        method: "POST",
        body: p
      })
    }),

    deleteReminder: builder.mutation({
      query: (p) => ({
        url: "/delete_reminder",
        method: "DELETE",
        params: { id: p.id }
      })
    })

  })

});

// Export the auto-generated hooks for the endpoints
export const { useGetHealthQuery, 
  useLaunchKYStartMutation, useLaunchKYFromInsideMutation,
  useLiveStreamModelCountsQuery, useLiveStreamsQuery,
  useCreateUserMutation, useLoginMutation, useAdminLoginMutation,
  useAdminRoleMutation, useEnable2faMutation, useCheck2faCodeMutation, useLoginCodeValidMutation,
  useUploadS3Mutation, useContestImagesQuery,
  useCastVoteMutation, useRemoveVoteMutation, useCountUserVotesQuery,
  useDeleteImageMutation, useEditImageMutation,
  useSendEmailMutation, useCheckCodeMutation,useGetConfigQuery, 
  useGetKYBQuery, useGetKYBImagesMutation, useUploadKYBS3Mutation, 
  useGetAllPartnersQuery, useUpdatePartnerMutation,
  useGetVerifyAccountCountsQuery, useGetVerifyAccountsByTypeQuery,
  useGetImagesMutation, useUpdateVerifyAccountStatusMutation,
  useGetAllMemberStatsQuery, useGetAllMembersForMutation,
  useGetStudioCountsQuery, useSearchMutation,
  useEditVerifyStatusMutation, useGetStatusHistoryMutation, useUploadExcelMutation,
  useGetPastAMLRunsQuery, useStartAccountUpdateMutation,
  useGetAMLBusinessHistoryQuery, useGetAMLIndividualHistoryQuery,
  useRunSingleAmlMutation, useGetFailedAccountsMutation,
  useGetPresignedURLMutation,   useGetKYBResultByAccountAndTypeMutation,
  useGetAllNotesQuery, useAddNoteMutation, useEditNoteMutation,
  useGetMentionableUsersQuery, usePostKYCSentMutation,
  useRunGetQuery, useRunPostMutation, useRunPostWithBodyMutation,
  useGetKYBResultsByPaymentStatusQuery, useGetIDVResultsWithSummaryQuery,
  useGetConfirmIDSummaryQuery, useGetConfirmIDDailyCountsQuery, useLazyGetConfirmIDImagesQuery,
  useGetRekognitionSummaryQuery, useGetRekognitionDailyCountsQuery,
  useUpdateProfileAssignmentMutation, useUpdateUserAssignmentMutation, useUpdateCompanyNameMutation,
  useCaptureContractMutation, useCaptureContractFromHtmlMutation,
  useGetAdminUsersQuery, useUpdateAdminRoleMutation, useGetKYCSentsMutation,
  useKybFormSubmissionMutation, useSendKYCEmailMutation, usePostAutoAMLMutation,
  useLazyExpiredIdenfyKYCQuery, useSendManualKYCEmailMutation, useSendManualKYBEmailMutation,
  useLazyGetExpiredKYBDocumentsQuery, useResetExpiredKYBDocumentsMutation,
  useGetUserDashboardQuery, usePostPaxumDataMutation,
  useLazyGetCountryPaymentsConfigQuery, useDeleteCountryPaymentConfigMutation, usePostCountryPaymentConfigMutation,
  useLazyGetBannedCountryPaymentsConfigQuery, useLazyGetCtoPMapsQuery, usePostEditCountryPaymentConfigMutation,
  useLazyGetCountryPaymentsConfigHistoryQuery, useLazyRunGetQuery,
  useUploadDacExcelMutation, useLazyGetDAC7TokenQuery, useLazyCheckDAC7TokenQuery, useLazyGetDAC7DataQuery,
  usePostDAC7SubmissionMutation, usePostAvailablePaymentConfigMutation, useLazyGetAvailablePaymentConfigsQuery,
  useSendDAC7EmailMutation, useGetDAC7ActivityQuery, useLazyGetDAC7ActivityQuery, useLazyGetAdBuysQuery,
  useLazyRunTest1Query, useLazySyncPaymentMMIQuery, useLazyGetKYBSubmissionJSONQuery,
  useLazyGetRemindersQuery, useInsertReminderMutation, useDeleteReminderMutation,
  useLazyGetIdenfyResponseQuery
 } = api;
